import { useState, ChangeEvent, FormEvent } from 'react';

interface FormField {
  value: string;
  error: string;
  validate: (value: string) => string;
}

interface UseFormReturn<T> {
  values: T;
  errors: Record<keyof T, string>;
  touched: Record<keyof T, boolean>;
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleBlur: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: FormEvent, onSubmit: (values: T) => void) => void;
  isValid: boolean;
  resetForm: () => void;
}

/**
 * A simple form validation hook
 * @param initialValues - Initial form values
 * @param config - Configuration for form validation
 * @returns Form state and handlers
 */
export function useFormValidation<T extends Record<string, string>>(
  initialValues: T,
  config: Record<keyof T, (value: string) => string>
): UseFormReturn<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Record<keyof T, string>>(() => {
    const initialErrors: Record<string, string> = {};
    Object.keys(initialValues).forEach(key => {
      initialErrors[key] = '';
    });
    return initialErrors as Record<keyof T, string>;
  });
  const [touched, setTouched] = useState<Record<keyof T, boolean>>(() => {
    const initialTouched: Record<string, boolean> = {};
    Object.keys(initialValues).forEach(key => {
      initialTouched[key] = false;
    });
    return initialTouched as Record<keyof T, boolean>;
  });

  const validateField = (name: keyof T, value: string): string => {
    if (config[name]) {
      return config[name](value);
    }
    return '';
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
    
    if (touched[name as keyof T]) {
      setErrors(prev => ({ 
        ...prev, 
        [name]: validateField(name as keyof T, value) 
      }));
    }
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ 
      ...prev, 
      [name]: validateField(name as keyof T, value) 
    }));
  };

  const handleSubmit = (e: FormEvent, onSubmit: (values: T) => void) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors: Record<string, string> = {};
    let isValid = true;
    
    Object.keys(values).forEach(key => {
      const error = validateField(key as keyof T, values[key as keyof T]);
      newErrors[key] = error;
      if (error) {
        isValid = false;
      }
    });
    
    setErrors(newErrors as Record<keyof T, string>);
    setTouched(Object.keys(touched).reduce((acc, key) => {
      acc[key as keyof T] = true;
      return acc;
    }, {} as Record<keyof T, boolean>));
    
    if (isValid) {
      onSubmit(values);
    }
  };

  const resetForm = () => {
    setValues(initialValues);
    setErrors(Object.keys(initialValues).reduce((acc, key) => {
      acc[key as keyof T] = '';
      return acc;
    }, {} as Record<keyof T, string>));
    setTouched(Object.keys(initialValues).reduce((acc, key) => {
      acc[key as keyof T] = false;
      return acc;
    }, {} as Record<keyof T, boolean>));
  };

  const isValid = Object.values(errors).every(error => !error);

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isValid,
    resetForm
  };
}

// Common validation functions
export const validators = {
  required: (value: string) => !value ? 'This field is required' : '',
  
  email: (value: string) => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !emailRegex.test(value) ? 'Invalid email address' : '';
  },
  
  minLength: (min: number) => (value: string) => 
    value.length < min ? `Must be at least ${min} characters` : '',
  
  maxLength: (max: number) => (value: string) => 
    value.length > max ? `Must be less than ${max} characters` : '',
    
  phone: (value: string) => {
    if (!value) return '';
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    return !phoneRegex.test(value.replace(/\s+/g, '')) ? 'Invalid phone number' : '';
  }
};
